import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';
import { FileExcelOutlined, LoadingOutlined } from '@ant-design/icons';
import type { HandsontableWrapperRef } from '../handsontable/HandsontableWrapperSimple';
import { exportToExcel } from '../../utils/excel';
import { appNotify } from '../../utils';
import styles from '../../styles/components/ExportButton.module.scss';
import { useUIConfig } from '../../contexts/UIConfigContext';

export interface ExportButtonProps {
  tableRef: React.RefObject<Pick<HandsontableWrapperRef, 'getHotInstance'> | null>;
  fileName?: string;
  sheetName?: string;
  disabled?: boolean;
  variant?: 'default' | 'compact' | 'modern';
  showLabel?: boolean;
  /**
   * Те же ключи UI Config, что используются TrackedTable —
   * чтобы Excel-шапка получила те же цвета, что в UI.
   */
  configSection?: string;
  configHeaderKey?: string;
  configNestedHeaderKey?: string;
  /**
   * Если у таблицы динамические колонки (БГ/менеджеры/месяцы),
   * включает fallback на последний цвет из конфига для индексов
   * за пределами заданных. Совпадает с логикой HoT-рендера.
   */
  dynamicHeaderColors?: boolean;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  tableRef,
  fileName,
  sheetName = 'Sheet1',
  disabled = false,
  variant = 'default',
  showLabel = false,
  configSection,
  configHeaderKey,
  configNestedHeaderKey,
  dynamicHeaderColors = false,
}) => {
  const { getButtonText, getHeaderColors } = useUIConfig();
  const exportText = getButtonText('export', 'Экспорт в Excel');
  const exportShortText = getButtonText('export_short', 'Экспорт');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!tableRef.current) {
      appNotify.error('Таблица не найдена');
      return;
    }

    const hotInstance = tableRef.current.getHotInstance();
    if (!hotInstance) {
      appNotify.error('Экземпляр таблицы недоступен');
      return;
    }

    setExporting(true);
    try {
      const leafColumn = configSection && configHeaderKey
        ? getHeaderColors(configSection, configHeaderKey)
        : undefined;
      const topLevel = configSection && configNestedHeaderKey
        ? getHeaderColors(configSection, configNestedHeaderKey)
        : undefined;

      exportToExcel(hotInstance, {
        fileName: fileName || 'export',
        sheetName,
        includeRowHeaders: true,
        headerColors:
          leafColumn || topLevel
            ? { leafColumn, topLevel, dynamicTopFallback: dynamicHeaderColors }
            : undefined,
      });
      appNotify.success('Экспорт выполнен успешно');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при экспорте';
      appNotify.error('Ошибка экспорта', errorMessage);
      if (import.meta.env.DEV) {
        console.error('Export error:', error);
      }
    } finally {
      setExporting(false);
    }
  };

  const buttonSize = 'small';
  const buttonClassName = `${styles.btn} ${variant === 'modern' ? styles.modern : ''}`;

  return (
    <Tooltip title={exportText} placement="bottom">
      <Button
        icon={exporting ? <LoadingOutlined /> : <FileExcelOutlined />}
        onClick={handleExport}
        disabled={disabled || exporting}
        loading={exporting}
        size={buttonSize}
        type="text"
        className={buttonClassName}
      >
        {showLabel && (exporting ? `${exportShortText}...` : exportShortText)}
      </Button>
    </Tooltip>
  );
};


